import { AxiosResponse } from "axios";
import { ICreateSolicitation, ISolicitation, IAuthUser } from "../lib/types";
import { createApiInstance } from "./api-interceptor";

export const BASE_URL_API_ARQUIVOS_LOCAL =
  import.meta.env.VITE_API_ARQUIVOS_LOCAL || null;
export const BASE_URL_PLAY = import.meta.env.VITE_API_URL || '/api';

// Usa a instância com interceptors de reautenticação
let apiInstance: ReturnType<typeof createApiInstance> | null = null;

export const backPlay = () => {
  if (!apiInstance) {
    apiInstance = createApiInstance();
  }
  return apiInstance;
};

export const login = async (
  email: string,
  password: string
): Promise<IAuthUser | null> => {
  try {
    const response = await backPlay().post<IAuthUser>("/login", {
      email,
      senha: password,
      perfil: "U",
    });
    return response.data;
  } catch (error) {
    console.error("Login error:", error);
    return null;
  }
};

export const sendEmailRecovery = async (
  email: string
): Promise<boolean | null> => {
  try {
    const response = await backPlay().post<boolean>(
      "/login/enviar-email-recuperacao-de-senha",
      {
        email,
        perfil: "U",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Login error:", error);
    return null;
  }
};

export const getReceivedSolicitations = async (
  organizationId: number,
  offset: number
): Promise<ISolicitation[]> => {
  try {
    const response = await backPlay().post(
      "/comunicacao/pesquisar-comunicacoes",
      {
        contexto: "dashboard_pendentes",
        empresaId: organizationId,
        limit: 10,
        mensagem: "",
        offset,
        orderBy: "solicitacao.created_at DESC",
      }
    );
    return response?.data ?? [];
  } catch (error) {
    console.error("Error fetching received solicitations:", error);
    return [];
  }
};

export const getSentSolicitations = async (
  organizationId: number,
  offset = 0
): Promise<ISolicitation[]> => {
  try {
    const response = await backPlay().post(
      "/comunicacao/pesquisar-comunicacoes",
      {
        contexto: "pendente_destino",
        empresaId: organizationId,
        limit: 10,
        mensagem: "",
        offset,
        orderBy: "solicitacao.created_at DESC",
      }
    );
    return response?.data ?? [];
  } catch (error) {
    console.error("Error fetching sent solicitations:", error);
    return [];
  }
};

function base64ToBlob(base64: string, mimeType: string): Blob {
  // Remove o prefixo "data:*/*;base64," se existir
  const cleanedBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

  const byteString = atob(cleanedBase64);
  const byteArray = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  return new Blob([byteArray], { type: mimeType });
}

export async function createSolicitation(data: ICreateSolicitation | any): Promise<any> { //Promise<ISolicitation>
  try {
    const formData = new FormData();
    if (!data?.mensagem)
      throw new Error('O campo mensagem é obrigatório!');

    for (const attr of Object.keys(data)) {
      if (attr === "files") {
        data[attr]?.forEach((file: any, index: string) => {
          // No web, convertemos base64 em Blob
          if (file.base64) {
            const blob = base64ToBlob(file.base64, file.mimeType || file.type || "application/octet-stream");
            const fileObject = new File([blob], file.name, { type: blob.type });
            formData.append(attr + index, fileObject);
          } else {
            // Em caso de já ser um File/Blob válido (ex: mobile)
            formData.append(attr + index, file);
          }
        });
        continue;
      }
      formData.append(attr, data[attr]);
    }

    const response: AxiosResponse<any> = await backPlay().postForm(
      "/comunicacao",
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export const getSolicitation = async (
  paiId: number,
  empresaId: number
): Promise<ISolicitation[]> => {
  try {
    const response = await backPlay().get(`/comunicacao/${paiId}/${empresaId}`);
    let data = response.data;

    if (BASE_URL_API_ARQUIVOS_LOCAL) {
      data = data.map((solicitation: ISolicitation) => {
        if (!solicitation.anexos) return solicitation;

        return {
          ...solicitation,
          anexos: solicitation.anexos.map((anexo) => ({
            ...anexo,
            file: {
              ...anexo.file,
              url: `${BASE_URL_API_ARQUIVOS_LOCAL}${anexo.file.url
                .split("/storage/")
                .pop()}`,
            },
          })),
        };
      });
    }

    return data;
  } catch (error) {
    console.error("Error fetching solicitation:", error);
    return [];
  }
};

export const getTemplate = async (clientId: number) => {
  try {
    const response = await backPlay().get(`/contato/aplicativo/${clientId}`);
    let data = response.data;

    if (BASE_URL_API_ARQUIVOS_LOCAL && data.file?.url) {
      data = {
        ...data,
        logo: `${BASE_URL_API_ARQUIVOS_LOCAL}${data.file.url
          .split("/storage/")
          .pop()}`,
      };
    }

    return data;
  } catch (error) {
    console.error("Error fetching template:", error);
    return null;
  }
};

export const setNotificationAsAttended = async (
  companyId: number,
  solicitationId: number
) => {
  try {
    const response = await backPlay().post(
      `/empresa/${companyId}/comunicacao/atender`,
      {
        id: solicitationId,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting notification as attended:", error);
    return null;
  }
};
